import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminListUsers,
  adminSetUserRole,
  adminMissingGroups,
  adminListSpaces,
  adminListTrash,
  adminRestore,
  adminSyncRuns,
  backfillAttachmentNames,
  type BackfillAttachmentNamesResult,
  type AdminSpacesQuery,
  type AdminSpacesResponse,
  type AdminUser,
  type MissingGroupProject,
  type TrashResponse,
  type SyncRunStatus,
} from '@/services/api/endpoints';
import {
  setProjectGroup,
  updateSpace,
  type SetProjectGroupArgs,
  type UpdateSpaceArgs,
} from '@/services/api/admin';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';

export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: adminListUsers,
    staleTime: 60 * 1000,
  });
}

export function useAdminMissingGroups() {
  return useQuery<MissingGroupProject[]>({
    queryKey: ['admin', 'missing-groups'],
    queryFn: adminMissingGroups,
    staleTime: 60 * 1000,
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation<void, Error, { oid: string; isAdmin: boolean }>({
    mutationFn: adminSetUserRole,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useSetProjectGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: SetProjectGroupArgs) => setProjectGroup(args),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'missing-groups'] });
      // Rattacher un groupe FAIT APPARAÎTRE l'espace (0029) : il entre dans la
      // liste des conteneurs, redevient éligible aux favoris, et l'inventaire
      // d'administration doit décrémenter son compteur « sans groupe 365 ». Sans
      // ces trois invalidations, la correction semble n'avoir aucun effet.
      qc.invalidateQueries({ queryKey: ['containers'] });
      qc.invalidateQueries({ queryKey: ['favorites'] });
      qc.invalidateQueries({ queryKey: ['admin', 'spaces'] });
    },
  });
}

export function useRenameSpace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: UpdateSpaceArgs) => {
      addBreadcrumb({ category: 'mutation', message: 'rename-space', data: args });
      return updateSpace(args);
    },
    onSuccess: () => {
      // The renamed name shows up wherever the container list is read (tree
      // tabs, search, favorites), so refresh both cached lists.
      qc.invalidateQueries({ queryKey: ['containers'] });
      qc.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}

/**
 * Inventaire de tous les espaces. `placeholderData` garde la page précédente
 * affichée pendant qu'une nouvelle recherche ou un nouveau tri se charge : sans
 * cela, le tableau se vide à chaque frappe et l'écran clignote.
 */
export function useAdminSpaces(query: AdminSpacesQuery) {
  return useQuery<AdminSpacesResponse>({
    queryKey: ['admin', 'spaces', query],
    queryFn: () => adminListSpaces(query),
    // Les compteurs (mails, volume) bougent dès qu'on range un mail : on ne les
    // garde pas longtemps, mais assez pour ne pas rappeler à chaque re-render.
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminTrash() {
  return useQuery<TrashResponse>({
    queryKey: ['admin', 'trash'],
    queryFn: adminListTrash,
    staleTime: 30 * 1000,
  });
}

export function useAdminSyncRuns() {
  return useQuery<SyncRunStatus[]>({
    queryKey: ['admin', 'sync-runs'],
    queryFn: adminSyncRuns,
    staleTime: 30 * 1000,
    // Rafraîchit périodiquement pour que « il y a X min » reste juste tant que
    // le panneau est ouvert.
    refetchInterval: 60 * 1000,
  });
}

export function useAdminRestore() {
  const qc = useQueryClient();
  return useMutation<void, Error, { kind: 'folder' | 'mail'; id: string }>({
    mutationFn: adminRestore,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'trash'] });
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['mails'] });
    },
  });
}

/**
 * Passe de rattrapage des noms de pièces jointes (mails rangés avant la colonne
 * 0024). Réentrante : chaque appel traite un lot et annonce ce qu'il reste, donc
 * on relance jusqu'à `done`.
 */
export function useBackfillAttachmentNames() {
  const qc = useQueryClient();
  return useMutation<BackfillAttachmentNamesResult, Error, number | undefined>({
    mutationFn: (limit) => backfillAttachmentNames(limit),
    onSuccess: () => {
      // La liste des mails porte les noms : elle doit être relue, sinon les
      // infobulles resteraient génériques jusqu'au prochain chargement.
      qc.invalidateQueries({ queryKey: ['mails'] });
    },
  });
}
